import { inputsConstants, siftConstants, alertConstants } from "../_constants";
import { execute_job, poll_job, download_from_s3, execute_sift_run, execute_quick_run } from "./basics.jobs";
import { fetch_weather, get_ele_tz } from "./weather-fetch.jobs";
import { siftService } from "../_services";
import { collect_shade_inputs } from "../../EnvironmentalShade";
import { run_shade_job } from "./canopy.job.js";
import fetchUtil from "../_services/fetchUtil";
import config from "config";

// Constants
const JOB_TYPES = {
  VALIDATION: "sift-validation",
  INIT: "sift-init",
  QUICK_RUN: "sift-single-run",
};

const STATUS_CODES = {
  SUCCESS: 100,
};

const VALIDATION_RUN_COUNT = 2;
const DEFAULT_SPI = 100;

async function handleWeatherSetup(inputs, dispatch) {
  if (inputs.weather_id !== undefined) {
    inputs.weather = inputs.weather_id;

    const ele_tz = await get_ele_tz([inputs.latitude, inputs.longitude]);
    if (ele_tz.data.ele.status === "OK") {
      inputs.elevation = ele_tz.data.ele.results[0].elevation;
    }
    if (ele_tz.data.tz.status === "OK") {
      inputs.timezone = ele_tz.data.tz.rawOffset / 3600;
    }
    if (ele_tz.data.loc.status === "OK") {
      inputs.location = ele_tz.data.loc.results[0].formatted_address;
    }
    return { success: true };
  }

  const weather_fetch_inputs = {
    weather_source: inputs.weather_source,
    site_features: inputs.site_features,
    elevation: inputs.elevation,
    timezone: inputs.timezone,
  };

  const weather_data = await fetch_weather(weather_fetch_inputs, JOB_TYPES.INIT).run(dispatch);

  if (weather_data.error) {
    return { success: false, error: weather_data };
  }

  Object.assign(inputs, {
    weather: weather_data.job_id,
    weather_id: weather_data.job_id,
    latitude: weather_data.latitude,
    longitude: weather_data.longitude,
    elevation: weather_data.elevation,
    timezone: weather_data.timezone,
    weather_source: weather_data.source,
  });

  return { success: true };
}

async function ensureLocationObject(inputs) {
  if (inputs.location_obj || !inputs.latitude || !inputs.longitude) {
    return;
  }

  const loc_obj_res = await siftService.getElevation([inputs.latitude, inputs.longitude]);

  if (loc_obj_res.data.loc.results.length > 0) {
    const addressComponents = loc_obj_res.data.loc.results[0]?.address_components;

    if (addressComponents) {
      inputs.location_obj = {
        city: addressComponents[0]?.long_name,
        state: addressComponents[1]?.long_name,
        country: addressComponents[2]?.long_name,
      };
    }
  }
}

async function handleShadeProcessing(inputs, dispatch) {
  if (inputs.en_shade_impact !== 1 || Object.values(inputs.shade_objects).length === 0) {
    return { success: true };
  }

  const shade_options = {
    latitude: inputs.map_center[0],
    longitude: inputs.map_center[1],
    elevation: 1,
    timeZone: inputs.timezone,
    shade_height_unit: inputs.shade_height_unit,
    shadow_day: "Jan 1 2021",
    type: "ground",
  };

  const shade_inputs = collect_shade_inputs(undefined, inputs.shade_objects, shade_options);
  const shade_resp = await run_shade_job(shade_inputs).run(dispatch);

  if (shade_resp.error) {
    console.error("Shade simulation error:", shade_resp.error);
    return { success: false, error: shade_resp.error };
  }

  inputs.env_shade_id = shade_resp.env_shade_id;
  inputs.shade_results = shade_resp;
  return { success: true };
}

function dispatchFailure(dispatch, error, message = null) {
  if (message) {
    dispatch({ type: alertConstants.ERROR, message });
  }
  return dispatch({
    type: siftConstants.GENERATE_RESULTS_FAILURE,
    error: typeof error === "string" ? error : error?.msg || error,
    failed: true,
    errors: error,
  });
}

export const run_quick_job = (inputs, action = undefined) => {
  return {
    job_id: undefined,
    job_string: "job_id",
    action: action,
    inputs: inputs,

    async run(dispatch) {
      try {
        inputs.job_id = undefined;
        inputs.env = config.env;
        inputs.run_count = 1;

        dispatch({ type: siftConstants.GET_RUNID_REQUEST });
        dispatch({ type: siftConstants.UPDATE_REPORT_DATA, key: "reset" });

        let res = await execute_quick_run(inputs);
        console.log("quick job response", res);

        if (res.error || !res.url) {
          return dispatchFailure(dispatch, res.error, "SIFT Encountered an error");
        }

        inputs.job_id = res.job_id;
        inputs.url = res.url;
        inputs.gcrs = [inputs.gcrs[0]];
        inputs.spis = [inputs.spis[0]];
        inputs.job_type = JOB_TYPES.QUICK_RUN;

        const sift_run_resp = await execute_sift_run(inputs);
        if (sift_run_resp?.error) {
          return dispatchFailure(dispatch, sift_run_resp.error, "SIFT Encountered an error");
        }

        const init_poll_resp = await poll_job(inputs, "sift-single-run", dispatch);

        if (init_poll_resp.error?.msg === "Cancelled") {
          return dispatchFailure(dispatch, init_poll_resp.error.msg, init_poll_resp.error.msg);
        }

        if (init_poll_resp.is_error) {
          console.error("Quick run error:", init_poll_resp);
          // return dispatchFailure(dispatch, init_poll_resp.error[1]);
        }

        if (!init_poll_resp.error?.msg && init_poll_resp.output?.results?.length > 0) {
          const layout_response = await get_layout(init_poll_resp.output.results[0].id);
          if (layout_response?.error) {
            return dispatchFailure(dispatch, layout_response.error);
          }

          // const download_json = await download_from_s3(layout_response);
          // if (download_json?.error) {
          //   return dispatchFailure(dispatch, download_json.error, "SIFT Encountered an error");
          // }

          const response = { layout: download_json, record: init_poll_resp.output.results[0] };
          dispatch({ type: siftConstants.GET_LAYOUT_SUCCESS, response });

          init_poll_resp.output.layout = download_json;
          init_poll_resp.output.meta = { ...inputs };

          dispatch({ type: inputsConstants.UPDATE_INPUT_BY_NAME, key: "res_generated", value: true });
          return dispatch({ type: siftConstants.GENERATE_RESULTS_SUCCESS, data: init_poll_resp.output });
        } else {
          return dispatchFailure(dispatch, init_poll_resp, "SIFT Encountered an error");
        }
      } catch (error) {
        console.error("Quick run unexpected error:", error);
        return dispatchFailure(dispatch, error, "SIFT Encountered an error");
      }
    },
  };
};

export const run_job = (inputs, action = undefined) => {
  return {
    job_id: undefined,
    job_string: "job_id",
    action: action,
    inputs: inputs,

    async run(dispatch) {
      // Initialize inputs
      inputs.job_id = undefined;
      inputs.env = config.env;

      if (inputs.images) {
        inputs.images = undefined;
      }

      // Trigger UI state to running
      dispatch({ type: siftConstants.GET_RUNID_REQUEST });
      dispatch({ type: siftConstants.UPDATE_REPORT_DATA, key: "reset" });

      if (!inputs.download_lock) {
        dispatch({ type: inputsConstants.UPDATE_INPUT_UI_STATE, key: "download_lock", value: true });
      }

      // Weather setup
      const weatherResult = await handleWeatherSetup(inputs, dispatch);
      if (!weatherResult.success) {
        return dispatchFailure(dispatch, weatherResult.error);
      }

      // Input corrections
      if (inputs.do_finance === 0) {
        inputs.analysis_period = 1;
      }

      await ensureLocationObject(inputs);

      // Shade processing (non-blocking - continues even if it fails)
      await handleShadeProcessing(inputs, dispatch);
      console.log("after 5", inputs);
      // Validation phase
      const validationResult = await this.runValidation(inputs, dispatch);
      if (!validationResult.success) {
        return dispatchFailure(dispatch, validationResult.error, validationResult.error);
      }

      // Main SIFT run
      if (validationResult.data.output.status === STATUS_CODES.SUCCESS) {
        return await this.runSiftMain(inputs, validationResult.data, dispatch);
      }
    },

    async runValidation(inputs, dispatch) {
      const job_inputs = {
        job_type: JOB_TYPES.VALIDATION,
        run_count: VALIDATION_RUN_COUNT,
      };

      const init_resp = await execute_job(job_inputs, JOB_TYPES.VALIDATION);

      if (init_resp.error) {
        return { success: false, error: init_resp.error };
      }

      dispatch({ type: siftConstants.RUN_CAN_CANCEL, can_cancel: true, job_id: init_resp.job_id });

      inputs.job_id = init_resp.job_id;
      inputs.url = init_resp.url;
      inputs.job_type = JOB_TYPES.VALIDATION;

      await execute_sift_run(inputs);
      console.log("final inputs", inputs);

      const init_poll_resp = await poll_job(inputs, JOB_TYPES.VALIDATION, dispatch);

      if (init_poll_resp.error?.msg === "Cancelled") {
        return { success: false, error: init_poll_resp.error.msg };
      }

      if (init_poll_resp.is_error) {
        return { success: false, error: init_poll_resp };
      }

      dispatch({ type: siftConstants.GENERATE_RESULTS_VALIDATED });
      inputs.result_handler = 1;

      return { success: true, data: init_poll_resp };
    },

    async runSiftMain(inputs, validationData, dispatch) {
      inputs.validation_job_id = inputs.job_id;

      if (inputs.simple_inverter === 1) {
        inputs.spis = [DEFAULT_SPI];
      }

      inputs.job_id = undefined;

      const job_inputs = {
        job_type: JOB_TYPES.INIT,
        run_count: inputs.gcrs.length * inputs.spis.length,
      };

      const run_resp = await execute_job(job_inputs, JOB_TYPES.INIT);

      inputs.job_id = run_resp.job_id;
      inputs.url = run_resp.url;
      inputs.job_type = JOB_TYPES.INIT;

      await execute_sift_run(inputs);

      dispatch({ type: siftConstants.RUN_SIFT_SUCCESS, job_id: run_resp.job_id });

      const run_poll_resp = await poll_job(inputs, JOB_TYPES.INIT, dispatch);

      if (run_poll_resp.error?.msg === "Cancelled") {
        dispatch({ type: alertConstants.ERROR, message: run_poll_resp.error.msg });
        return dispatchFailure(dispatch, run_poll_resp.error.msg);
      }

      dispatch({ type: siftConstants.RUN_CAN_CANCEL, can_cancel: false });

      if (!run_poll_resp.error?.msg && run_poll_resp.output.results.length > 0) {
        const layout_response = await get_layout(run_poll_resp.output.results[0].id);
        const download_json = await download_from_s3(layout_response);

        const response = { layout: download_json, record: run_poll_resp.output.results[0] };
        dispatch({ type: siftConstants.GET_LAYOUT_SUCCESS, response });

        run_poll_resp.output.layout = download_json;
        run_poll_resp.output.meta = { ...inputs };

        dispatch({ type: inputsConstants.UPDATE_INPUT_BY_NAME, key: "res_generated", value: true });
        return dispatch({ type: siftConstants.GENERATE_RESULTS_SUCCESS, data: run_poll_resp.output });
      } else {
        return dispatchFailure(dispatch, run_poll_resp);
      }
    },
  };
};

async function get_layout(id) {
  const requestOptions = {
    method: "GET",
  };
  return fetchUtil(`${config.apiUrl}/dash/swm/layout?resultId=${id}`, requestOptions);
}
